import { Clipboard } from "@capacitor/clipboard";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import QRCode from "qrcode.react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import BlockAccordion from "@components/accordions/BlockAccordion";
import styles from "@components/account/Account.module.css";
import Button from "@components/buttons/Button";
import Toggle from "@components/buttons/Toggle";
import PhoneNumberForm from "@components/forms/PhoneNumberForm";
import TwoFactorCodeForm from "@components/forms/TwoFactorCodeForm";
import Nbsp from "@components/Nbsp";
import AlertPopover from "@components/popovers/AlertPopover";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import Heading3 from "@components/typography/Heading3";
import useTwoFactorAuthenticator from "@hooks/useTwoFactorAuthenticator";
import { TwoFactorSteps } from "@typing/Enums";
import { GetClientWithAccountQuery, PhoneNumberChannelEnum, TwoFactorAuthenticationTypeEnum } from "@typing/Generated";

type Props = {
  client: Pick<
    GetClientWithAccountQuery["client"],
    "id" | "verificationPhoneNumber" | "twoFactorType" | "lastOtpAt" | "totpSecret"
  >;
};

type TypeHeaderProps = {
  type: TwoFactorAuthenticationTypeEnum;
};

const TypeHeader = ({ type }: TypeHeaderProps) => (
  <div>
    <div>
      <BodyCopy bold color="gray">
        <FormattedMessage id={`pages.client.account.twoFactor.${type}.title`} />
      </BodyCopy>
      <div>
        {type === TwoFactorAuthenticationTypeEnum.TOTP && (
          <BodyCopy color="success" size="small">
            <FormattedMessage id="pages.client.account.twoFactor.recommended" />
            <Nbsp />
            <Nbsp />
          </BodyCopy>
        )}

        <BodyCopy color="gray-500" size="small">
          <FormattedMessage id={`pages.client.account.twoFactor.${type}.description`} />
        </BodyCopy>
      </div>
    </div>
  </div>
);

type TwoFactorTypeProps = {
  client: Props["client"];
  twoFactorAuthenticator: ReturnType<typeof useTwoFactorAuthenticator>;
};

const TwoFactorTOTP = ({ client, twoFactorAuthenticator }: TwoFactorTypeProps) => {
  const handleCopyTotpKey = () => {
    Clipboard.write({ string: client.totpSecret ?? "" });
  };

  return (
    <div>
      {twoFactorAuthenticator.step === TwoFactorSteps.SETUP_TYPE ? (
        <>
          <Heading3 bold color="gray">
            <FormattedMessage id="pages.client.account.twoFactor.TOTP.instructions" />
          </Heading3>
          <div className="flex flex-column gap-md">
            <div className="flex flex-column gap-sm">
              <BodyCopy color="gray">
                <FormattedMessage id="pages.client.account.twoFactor.TOTP.step1" />
              </BodyCopy>
              <BodyCopy color="gray-500" size="small">
                <FormattedMessage id="pages.client.account.twoFactor.TOTP.step1Description" />
              </BodyCopy>
            </div>
            <div className="flex flex-column gap-sm">
              <BodyCopy color="gray">
                <FormattedMessage id="pages.client.account.twoFactor.TOTP.step2" />
              </BodyCopy>
              <BodyCopy color="gray-500" size="small">
                <FormattedMessage id="pages.client.account.twoFactor.TOTP.step2Description" />
              </BodyCopy>
              <div>
                {twoFactorAuthenticator.provisioningUri && (
                  <IonGrid>
                    <IonRow className="ion-align-items-center">
                      <IonCol size="auto" sizeMd="auto" sizeSm="auto" sizeXs="12">
                        <div className={styles.qrCode}>
                          <QRCode level="H" size={140} value={twoFactorAuthenticator.provisioningUri} />
                        </div>
                      </IonCol>
                      <IonCol className="ion-text-center" size="auto" sizeSm="auto" sizeXs="12">
                        <div className={styles.keyMaxWidth}>
                          <BodyCopy>{client.totpSecret}</BodyCopy>
                          <div className="ion-margin-top">
                            <AlertPopover textKey="dictionary.copied">
                              <ButtonLink onClick={handleCopyTotpKey}>
                                <FormattedMessage id="dictionary.copy" />
                              </ButtonLink>
                            </AlertPopover>
                          </div>
                        </div>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                )}
              </div>
            </div>
            <div className="flex flex-column gap-sm">
              <BodyCopy color="gray">
                <FormattedMessage id="pages.client.account.twoFactor.TOTP.step3" />
              </BodyCopy>
              <BodyCopy color="gray-500" size="small">
                <FormattedMessage id="pages.client.account.twoFactor.TOTP.step3Description" />
              </BodyCopy>
            </div>
          </div>
          <div className="ion-margin-top align-center">
            <Button
              onClick={() => {
                twoFactorAuthenticator.setStep(TwoFactorSteps.VERIFY_CODE);
              }}
            >
              <FormattedMessage id="dictionary.next" />
            </Button>
          </div>
        </>
      ) : (
        <div className="ion-margin-top">
          <BodyCopy size="small">
            <FormattedMessage id="pages.client.account.twoFactor.TOTP.inputCode" />
          </BodyCopy>

          <div className="ion-text-center">
            <TwoFactorCodeForm onSuccess={twoFactorAuthenticator.handleVerificationSuccess} />
          </div>
        </div>
      )}
    </div>
  );
};

const TwoFactorSMS = ({ client, twoFactorAuthenticator }: TwoFactorTypeProps) => (
  <div>
    {twoFactorAuthenticator.step === TwoFactorSteps.SETUP_TYPE || !client.verificationPhoneNumber ? (
      <>
        <div className={styles.sectionDescription}>
          <BodyCopy size="small">
            <FormattedMessage id="pages.client.account.twoFactor.description" />
          </BodyCopy>
        </div>
        <div className="ion-text-center">
          <PhoneNumberForm onSuccess={twoFactorAuthenticator.handlePhoneNumberSuccess} />
        </div>
      </>
    ) : (
      <div>
        <div className="ion-margin-top">
          <BodyCopy size="small">
            <FormattedMessage
              id="pages.client.account.twoFactor.SMS.inputCode"
              values={{ number: client.verificationPhoneNumber?.formattedNumber }}
            />
          </BodyCopy>

          <div className="ion-text-center">
            <TwoFactorCodeForm onSuccess={twoFactorAuthenticator.handleVerificationSuccess} />
          </div>
        </div>
        <div className={`${styles.spacing} ion-text-center`}>
          <FormattedMessage id="pages.client.account.twoFactor.noCode" />
        </div>
        <div className="flex justify-center items-center">
          <div className="ion-margin-end">
            <Button
              fill="clear"
              onClick={() => {
                twoFactorAuthenticator.resendCode(PhoneNumberChannelEnum.SMS);
              }}
            >
              <Icon icon="repeat" slot="start" />
              <FormattedMessage id="pages.client.account.twoFactor.resendCode" />
            </Button>
          </div>
          <div>
            <Button
              fill="clear"
              onClick={() => {
                twoFactorAuthenticator.resendCode(PhoneNumberChannelEnum.CALL);
              }}
            >
              <Icon icon="phone" slot="start" />
              <FormattedMessage id="pages.client.account.twoFactor.receiveCall" />
            </Button>
          </div>
        </div>
      </div>
    )}
  </div>
);

const TwoFactorAuthenticationSetup = ({ client }: Props) => {
  const intl = useIntl();
  const [twoFactor, setTwoFactor] = useState(client.twoFactorType ? true : false);
  const twoFactorAuthenticator = useTwoFactorAuthenticator({ client, settingUp: true });

  return (
    <div className="ion-padding">
      <IonGrid className="ion-no-padding">
        <IonRow>
          <IonCol>
            <Heading2 bold color="black">
              <FormattedMessage id="pages.client.account.privacyAndAccount.sections.twoFactor" />
              {client.twoFactorType && (
                <>
                  <Icon className={styles.verticalAlign} color="success" icon="checkmark" />
                  <div>
                    <BodyCopy>
                      <FormattedMessage id={`pages.client.account.twoFactor.${client.twoFactorType}.title`} />
                    </BodyCopy>
                  </div>
                </>
              )}
            </Heading2>
          </IonCol>
          <IonCol size="auto">
            <Toggle
              checked={twoFactor}
              label={intl.formatMessage({ id: twoFactor ? "dictionary.on" : "dictionary.off" })}
              setValue={v => {
                if (!v) {
                  twoFactorAuthenticator.handleRemoveTwoFactor();
                }
                setTwoFactor(v);
              }}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
      {twoFactor && !client.twoFactorType && (
        <div>
          <div className="ion-margin-top">
            <BodyCopy bold color="gray">
              <FormattedMessage id="pages.client.account.twoFactor.howToGetCodes" />
            </BodyCopy>

            <div className={styles.topSpacing}>
              <div className={styles.twoFactorOptions}>
                <BodyCopy color="gray-300" size="small">
                  <FormattedMessage id="pages.client.account.twoFactor.options" />
                </BodyCopy>
              </div>
              <BlockAccordion
                border={false}
                color="white"
                content={<TwoFactorTOTP client={client} twoFactorAuthenticator={twoFactorAuthenticator} />}
                heading={<TypeHeader type={TwoFactorAuthenticationTypeEnum.TOTP} />}
                onIonChange={e => {
                  if (e.detail.value) {
                    twoFactorAuthenticator.handleTOTPAuthenticationCreate();
                  }
                }}
              />
              <BlockAccordion
                border={false}
                color="white"
                content={<TwoFactorSMS client={client} twoFactorAuthenticator={twoFactorAuthenticator} />}
                heading={<TypeHeader type={TwoFactorAuthenticationTypeEnum.SMS} />}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFactorAuthenticationSetup;
